import * as React from "react";
import LoadingState from "../../components/atoms/Loading/LoadingState";
import Content from "../../components/organisms/Content/Content";
import { getTeam } from "./actions";
import { throwHttpErros, catchHttpErros } from "../../utils/httpErrors";
import { Group, TeamMember } from "./types";
import Table from "../../components/organisms/Table/Table";
import Tag from "../../components/atoms/Tag/Tag";
import HeadlineOptions from "../../components/molecules/HeadlineOptions/HeadlineOptions";
import Button from "../../components/atoms/Button/Button";
import AddTeamMember from "./AddTeamMember/AddTeamMember";
import EditTeamMember from "./EditTeamMember/EditTeamMember";
import DeleteTeamMember from "./DeleteTeamMember/DeleteTeamMember";
import { GroupContext } from "../Groups/context";
import { TokenPayload } from "../Auth/context";

interface Props {
  user: TokenPayload;
}

interface State {
  isLoading: boolean;
  modal: "add" | "edit" | "delete" | null;
  modalEntity: TeamMember | null;
  team: TeamMember[];
}

class Team extends React.Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context);

    this.state = {
      isLoading: true,
      modal: null,
      modalEntity: null,
      team: []
    };
  }

  public getColumns = (rows: TeamMember[]) => {
    return [
      {
        title: "Benutzer",
        dataIndex: "username",
        key: "username"
      },
      {
        title: "E-Mail",
        dataIndex: "email",
        key: "email"
      },
      {
        title: "Gruppen",
        dataIndex: "groups",
        key: "groups",
        filters: rows
          .reduce((list: string[], item: TeamMember) => {
            const newGroups = item.groups
              .map(group => group.name)
              .filter(x => !list.includes(x));
            return [...list, ...newGroups];
          }, [])
          .map(group => ({ text: group, value: group })),
        onFilter: (filter: string, record: any) => {
          return record.groups.find((x: Group) => x.name === filter);
        },
        render: (groups: Group[]) => (
          <>
            {groups.map(group => (
              <Tag key={group.ID}>{group.name}</Tag>
            ))}
          </>
        )
      },
      {
        title: "Admin",
        dataIndex: "admin",
        key: "admin",
        render: (isAdmin: boolean) => <>{isAdmin ? "Ja" : ""}</>
      },
      {
        title: "Aktionen",
        dataIndex: "entity",
        key: "entity",
        width: 250,
        render: (entity: TeamMember) =>
          entity.admin ? (
            <>
              {entity.ID === this.props.user.ID && (
                <Button
                  type="default"
                  onClick={this.onOpenEditModal.bind(this, entity)}
                >
                  Bearbeiten
                </Button>
              )}
            </>
          ) : (
            <>
              {
                <Button
                  type="default"
                  onClick={this.onOpenEditModal.bind(this, entity)}
                >
                  Bearbeiten
                </Button>
              }
              <Button
                type="danger"
                onClick={this.onOpenDeleteModal.bind(this, entity.ID)}
              >
                Löschen
              </Button>
            </>
          )
      }
    ];
  };

  public mapRows = (rows: TeamMember[]) => {
    return rows.map((row: TeamMember) => {
      return {
        key: row.ID,
        username: row.username,
        email: row.email,
        admin: row.admin,
        groups: row.groups,
        entity: row
      };
    });
  };

  public componentDidMount(): void {
    this.fetchTeam();
  }

  public fetchTeam = () => {
    getTeam()
      .then(throwHttpErros)
      .then(response => {
        this.setState({
          isLoading: false,
          team: response
        });
      })
      .catch(catchHttpErros);
  };

  public onCloseModal = () => {
    this.setState({ modal: null, modalEntity: null });
  };

  public onOpenAddModal = () => {
    this.setState({ modal: "add" });
  };

  public onOpenDeleteModal = (id: string) => {
    const modalEntity = this.state.team.find(x => x.ID === id) || null;
    this.setState({ modal: "delete", modalEntity });
  };

  public onOpenEditModal = (modalEntity: TeamMember) => {
    this.setState({ modal: "edit", modalEntity });
  };

  public onDone = () => {
    this.onCloseModal();
    this.fetchTeam();
  };

  public renderModal = () => {
    switch (this.state.modal) {
      default:
        return null;
      case "add":
        return (
          <GroupContext.Consumer>
            {context =>
              context && context.groups.length > 0 ? (
                <AddTeamMember groups={context.groups} onDone={this.onDone} />
              ) : null
            }
          </GroupContext.Consumer>
        );
      case "delete":
        const { modalEntity } = this.state;
        return (
          modalEntity && (
            <DeleteTeamMember teamMember={modalEntity} onDone={this.onDone} />
          )
        );
      case "edit":
        return (
          <GroupContext.Consumer>
            {context =>
              context && context.groups.length > 0 && this.state.modalEntity ? (
                <EditTeamMember
                  user={this.state.modalEntity}
                  groups={context.groups}
                  onDone={this.onDone}
                />
              ) : null
            }
          </GroupContext.Consumer>
        );
    }
  };

  public render() {
    const { isLoading } = this.state;

    return (
      <Content>
        <HeadlineOptions
          left={<h1>Team</h1>}
          right={
            <Button
              type="primary"
              size="small"
              disabled={this.state.modal === "add"}
              onClick={this.onOpenAddModal}
            >
              Hinzufügen
            </Button>
          }
        />
        {isLoading ? (
          <LoadingState size="l" text="Lade Team" />
        ) : (
          <>
            {this.renderModal()}
            <Table
              columns={this.getColumns(this.state.team)}
              dataSource={this.mapRows(this.state.team)}
            />
          </>
        )}
      </Content>
    );
  }
}

export default Team;
